import { useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import { AttributeFormModal } from './AttributeFormModal'
import { useAttributes, type AttributeDto } from '../../../viewmodels/useAttributes'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /manage/attributes — list + create/edit/delete.
 *
 * Mirrors src/ui/pages/iam/UserTypesPage.tsx — same header / table /
 * empty state / modal flow. The Attributes screen is the first
 * Manage-domain CRUD screen.
 */
export function AttributesPage(): JSX.Element {
  const {
    items,
    datatypes,
    customDataTypes,
    total,
    page,
    pageSize,
    isLoading,
    error,
    setPage,
    reload,
    create,
    update,
    remove
  } = useAttributes()
  const { can } = useAuth()
  const [modalMode, setModalMode] = useState<'closed' | 'create' | 'edit'>('closed')
  const [editTarget, setEditTarget] = useState<AttributeDto | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const openCreate = (): void => {
    setEditTarget(null)
    setModalMode('create')
  }
  const openEdit = (row: AttributeDto): void => {
    setEditTarget(row)
    setModalMode('edit')
  }
  const closeModal = (): void => setModalMode('closed')

  const handleDelete = async (row: AttributeDto): Promise<void> => {
    const confirmMsg = 'Are you sure you want to delete?'
    if (typeof window !== 'undefined' && !window.confirm(confirmMsg)) return
    setDeletingId(row.id)
    setDeleteError(null)
    const res = await remove(row.id)
    setDeletingId(null)
    if (!res.ok) setDeleteError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-h2 text-charcoal">Attributes</h1>
        {can(SCREENCODES.MANAGEATTRIBUTES, 'add') ? <Button onClick={openCreate}>Add</Button> : null}
      </header>

      {deleteError !== null ? <Alert tone="error">{deleteError}</Alert> : null}
      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => void reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Table
            items={items}
            onEdit={openEdit}
            onDelete={(row) => void handleDelete(row)}
            deletingId={deletingId}
          />
          <Paginator
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </>
      )}

      <AttributeFormModal
        open={modalMode !== 'closed'}
        mode={modalMode === 'edit' ? 'edit' : 'create'}
        initial={editTarget}
        datatypes={datatypes}
        customDataTypes={customDataTypes}
        onClose={closeModal}
        onSubmit={async (input) => {
          if (modalMode === 'edit' && editTarget !== null) {
            return update(editTarget.id, input)
          }
          return create(input as Parameters<typeof create>[0])
        }}
      />
    </div>
  )
}

/**
 * EmptyState — purely informational. The Add button lives in the page
 * header (top-right), always present. Duplicating it here adds visual
 * noise without giving the operator a path the header doesn't already
 * provide.
 */
function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No attributes yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Use <span className="font-medium">Add</span> at the top right to declare
        the first attribute.
      </p>
    </div>
  )
}

function Table({
  items,
  onEdit,
  onDelete,
  deletingId
}: {
  readonly items: AttributeDto[]
  readonly onEdit: (row: AttributeDto) => void
  readonly onDelete: (row: AttributeDto) => void
  readonly deletingId: string | null
}): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.MANAGEATTRIBUTES, 'edit')
  const canDelete = can(SCREENCODES.MANAGEATTRIBUTES, 'delete')
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Name</Th>
            <Th>Data type</Th>
            <Th>Description</Th>
            <Th>Products</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const locked = row.linked_product_count > 0
            const lockTooltip = locked
              ? `Locked: ${row.linked_product_count} active product${row.linked_product_count === 1 ? '' : 's'} use${row.linked_product_count === 1 ? 's' : ''} this attribute. Rename + description are still allowed.`
              : ''
            return (
              <tr key={row.id} className="border-t border-border">
                <Td>{row.name}</Td>
                <Td>
                  <span className="font-mono text-text-secondary">{row.datatype_name}</span>
                  {row.custom_data_type_id !== null ? (
                    <span className="ml-2 text-text-secondary">
                      →{' '}
                      {row.custom_data_type_name !== null ? (
                        <span className="font-mono">{row.custom_data_type_name}</span>
                      ) : (
                        <span className="italic text-terra" title="Linked custom data type has been deleted">
                          (deleted)
                        </span>
                      )}
                    </span>
                  ) : null}
                </Td>
                <Td
                  className="max-w-[24rem] truncate"
                  title={row.description === null ? '' : row.description}
                >
                  {row.description ?? <span className="text-text-secondary">—</span>}
                </Td>
                <Td>
                  {locked ? (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full bg-bamboo/20 text-charcoal font-medium text-body-xs"
                      title={lockTooltip}
                    >
                      {row.linked_product_count} product{row.linked_product_count === 1 ? '' : 's'}
                    </span>
                  ) : (
                    <span className="text-text-secondary">—</span>
                  )}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  {canEdit ? (
                    <>
                      <Button size="sm" variant="secondary" onClick={() => onEdit(row)}>
                        Edit
                      </Button>{' '}
                    </>
                  ) : null}
                  {canDelete ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDelete(row)}
                      disabled={deletingId === row.id || locked}
                      title={locked ? lockTooltip : ''}
                    >
                      {deletingId === row.id ? 'Deleting…' : 'Delete'}
                    </Button>
                  ) : null}
                </Td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </TableShell>
  )
}
